import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import type { ApiErrorResponse } from '../types/api';

// Frontend and backend now run on the same Node host, so the API lives
// at the same origin under /api. In CRA dev mode we still need to point
// at the local Express on :5001 (see frontend/src/setupProxy.js for an
// alternative). REACT_APP_API_URL can override this for one-off envs.
const API_BASE = process.env.REACT_APP_API_URL
  || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5001/api');

const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' }
});

// Derive the tenant school code from the current browser URL and forward
// it on every request. Even though the API now runs on the same origin
// as the page, the X-Tenant-Code header keeps two niceties: it lets the
// dev override (?tenant=CODE) work without subdomain DNS, and it's a
// fallback if a request hits the API through a proxy that strips Host.
const BARE_SUBDOMAINS = new Set(['www', 'api', 'app', 'admin']);
const TENANT_HOSTS = /\.(edu\.rtconnect\.in|edumanage\.in)$/i;

function resolveTenantCode(): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const override = params.get('tenant');
    if (override) return override.toUpperCase();

    const host = window.location.hostname;
    if (!TENANT_HOSTS.test(host)) return null;
    const first = host.split('.')[0];
    if (!first || BARE_SUBDOMAINS.has(first.toLowerCase())) return null;
    return first.toUpperCase();
  } catch {
    return null;
  }
}

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const tenantCode = resolveTenantCode();
  if (tenantCode) {
    config.headers['X-Tenant-Code'] = tenantCode;
  }
  return config;
});

// On 401, drop the session and redirect to login. All other errors bubble up.
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiErrorResponse>) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('school');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;
